import React from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import useEditableSection from "../hooks/useEditableSection";

// Renders a whole section (heading + field list) behind a single
// Edit/Save/Cancel control, matching the Edit/Save/Close pattern already
// used in Edit_client.js (pi-user-edit / pi-save / pi-times, help/success/danger).
//
// `fields` is the single source of truth for the section's rows - each entry
// is either read-only ({ key, label, value }) or editable
// ({ key, label, value, editable: true, validate }). Adding a new editable
// field later is just adding an entry with `editable: true` here; no new
// component logic is needed.
const EditableSection = ({
    title,
    fields,
    onSave,
    headingTag: Heading = "h6",
    labelTag: Label = "span",
    listClassName = "clt_acDetails mt-4 mb-5",
    emptyText = "N/A",
}) => {
    const editableFields = fields.filter((field) => field.editable);
    const {
        isEditing,
        values,
        errors,
        saving,
        startEdit,
        cancelEdit,
        save,
        setFieldValue,
    } = useEditableSection(editableFields, onSave);

    return (
        <>
            <div className="d-flex justify-content-between align-items-center">
                <Heading className="mb-0">{title}</Heading>
                <div>
                    {isEditing && (
                        <Button
                            icon="pi pi-times"
                            label="Cancel"
                            severity="danger"
                            onClick={cancelEdit}
                            type="button"
                            text
                            disabled={saving}
                            className="me-2"
                        />
                    )}
                    <Button
                        icon={isEditing ? (saving ? "pi pi-spin pi-spinner" : "pi pi-save") : "pi pi-user-edit"}
                        label={isEditing ? "Save" : "Edit"}
                        severity={isEditing ? "success" : "help"}
                        onClick={isEditing ? save : startEdit}
                        type="button"
                        text
                        disabled={saving}
                    />
                </div>
            </div>
            <hr />
            {errors._section && <small className="p-error d-block mb-2">{errors._section}</small>}
            <ul className={listClassName}>
                {fields.map((field) => (
                    <li key={field.key}>
                        <Label>{field.label}</Label>
                        {field.editable && isEditing ? (
                            <>
                                <InputText
                                    value={values[field.key] ?? ""}
                                    onChange={(e) => setFieldValue(field.key, e.target.value)}
                                    className={`p-1 ${errors[field.key] ? "p-invalid" : ""}`}
                                    disabled={saving}
                                    autoFocus={field.key === editableFields[0]?.key}
                                />
                                {errors[field.key] && <small className="p-error d-block mt-1">{errors[field.key]}</small>}
                            </>
                        ) : (
                            field.value || emptyText
                        )}
                    </li>
                ))}
            </ul>
        </>
    );
};

export default EditableSection;
